import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../../Icon/Icon';
import { Tooltip } from '../../Tooltip/Tooltip';

/**
 * ChatInput - Input form component for Gigabot
 *
 * Props:
 * - value: string - Current input value
 * - onChange: function - Callback when input changes
 * - onSubmit: function - Callback when form is submitted
 * - onKeyDown: function - Callback for keydown events
 * - placeholder: string - Input placeholder text
 * - disabled: boolean - Whether input is disabled
 * - isLoading: boolean - Whether a message is being sent
 * - style: object - Additional styles for textarea
 * - availableModels: array - List of available AI models
 * - selectedModel: string - Currently selected model ID
 * - onModelChange: function - Callback when model is changed
 * - webSearchEnabled: boolean - Whether web search is enabled
 * - onWebSearchChange: function - Callback when web search is toggled
 * - showWebSearch: boolean - Whether to show web search toggle
 */
export const ChatInput = forwardRef(
  (
    {
      value,
      onChange,
      onSubmit,
      onKeyDown,
      placeholder = 'Ask me anything...',
      disabled = false,
      isLoading = false,
      style = {},
      availableModels = [],
      selectedModel,
      onModelChange,
      webSearchEnabled = false,
      onWebSearchChange,
      showWebSearch = false,
    },
    ref
  ) => {
    const hasBlank = value.includes('_____');

    const defaultStyle = {
      minHeight: '40px',
      maxHeight: '200px',
      resize: 'none',
      overflow: 'auto',
      border: 'none',
      outline: 'none',
      boxShadow: 'none',
    };

    const mergedStyle = { ...defaultStyle, ...style };

    return (
      <>
        {/* Model Selection and Web Search */}
        <div className='gigabot-model-selector'>
          {showWebSearch && onWebSearchChange && (
            <Tooltip
              content={
                webSearchEnabled ? 'Disable web search' : 'Enable web search'
              }
              position='top'
            >
              <button
                type='button'
                className={`web-search-toggle ${
                  webSearchEnabled ? 'active' : ''
                }`}
                onClick={() => onWebSearchChange(!webSearchEnabled)}
                disabled={isLoading}
                aria-label='Toggle web search'
              >
                <Icon name='Search_Magnifying_Glass' size='xs' />
              </button>
            </Tooltip>
          )}
          {availableModels.length > 0 && (
            <>
              <select
                id='model-select'
                value={selectedModel}
                onChange={(e) => onModelChange(e.target.value)}
                className='model-dropdown'
                disabled={isLoading}
              >
                {availableModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        <form
          className='gigabot-input'
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className='gigabot-input-container'>
            <textarea
              ref={ref}
              placeholder={placeholder}
              aria-label='Gigabot message'
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              disabled={disabled}
              spellCheck='false'
              rows={1}
              style={mergedStyle}
            />
            <button
              type='submit'
              className='send-button'
              disabled={!value.trim() || isLoading}
              aria-label={hasBlank ? 'Next field' : 'Send message'}
            >
              <Icon name='Arrow_Right_MD' size='xs' />
            </button>
          </div>
        </form>
      </>
    );
  }
);

ChatInput.displayName = 'ChatInput';

ChatInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  style: PropTypes.object,
  availableModels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedModel: PropTypes.string,
  onModelChange: PropTypes.func,
  webSearchEnabled: PropTypes.bool,
  onWebSearchChange: PropTypes.func,
  showWebSearch: PropTypes.bool,
};

export default ChatInput;
